
import { AdminLayout } from '@/components/admin/layout/AdminLayout';
import { Loader2, ShieldAlertIcon } from 'lucide-react';
import { HeaderActions } from '@/components/admin/users/HeaderActions';
import { DemoModeAlert } from '@/components/admin/users/DemoModeAlert';
import { ErrorAlert } from '@/components/admin/users/ErrorAlert';
import { EmptyStateAlert } from '@/components/admin/users/EmptyStateAlert';
import { DebugInfoPanel } from '@/components/admin/users/DebugInfoPanel';
import { UserTabs } from '@/components/admin/users/UserTabs';
import { getInitials, formatDate } from '@/components/admin/users/utils/userHelpers';
import { useUserManagement } from '@/components/admin/users/hooks/useUserManagement';
import { useAuth } from '@/contexts/AuthContext';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useAdminStatus } from '@/hooks/useAdminStatus';

const AdminUsers = () => {
  const {
    users,
    filteredUsers,
    recentUsers,
    adminUsers,
    adminUserIds,
    searchQuery,
    setSearchQuery,
    isLoading: usersLoading,
    isError,
    error,
    refetch,
    handleAdminStatusChange,
    handleRefresh,
    showDebugInfo,
    toggleDebugInfo,
    debugData
  } = useUserManagement();

  const { user } = useAuth();
  const { isSuperAdmin, isLoading: adminLoading } = useAdminStatus();
  const isLoading = usersLoading || adminLoading;

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="flex h-64 items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </AdminLayout>
    );
  }

  if (!isSuperAdmin) {
    return (
      <AdminLayout>
        <div className="space-y-6">
          <Alert className="bg-red-50 border-red-200">
            <ShieldAlertIcon className="h-4 w-4 text-red-500" />
            <AlertTitle className="text-red-800">Access Restricted</AlertTitle>
            <AlertDescription className="text-red-700">
              <p className="mb-2">
                This section is restricted to super admin access only.
              </p>
              <p>
                Please contact the system administrator if you believe you should have access to this page.
              </p>
            </AlertDescription>
          </Alert>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <DemoModeAlert />

        <HeaderActions 
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          handleRefresh={handleRefresh}
          showDebugInfo={showDebugInfo}
          toggleDebugInfo={toggleDebugInfo}
        />

        <ErrorAlert 
          isError={isError} 
          error={error} 
          handleRefresh={handleRefresh} 
        />

        <DebugInfoPanel 
          showDebugInfo={showDebugInfo} 
          debugData={debugData} 
          handleRefresh={handleRefresh} 
        />

        <EmptyStateAlert 
          isEmpty={users?.length === 0}
          isLoading={isLoading}
          isError={isError}
          refetch={refetch}
        />

        {users && users.length > 0 && (
          <UserTabs 
            users={users}
            filteredUsers={filteredUsers}
            recentUsers={recentUsers}
            adminUsers={adminUserIds}
            onAdminStatusChange={handleAdminStatusChange}
            getInitials={getInitials}
            formatDate={formatDate}
          />
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminUsers;
